import {
  TypeLayout,
  TypeLayoutBody,
  TypeLayoutCell,
  TypeLayoutRow,
} from 'components/type-layout/type-layout';
import { Output } from '../calculation/output';
import { DepthRange } from './depth_range';
import {
  Button,
  InlineNotification,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { OutputSkeleton } from '../../common/output_skeleton';
import { CalculationLoading } from '../../common/calculation-loading';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { TractResponse } from 'types/api-responses';
import { CenteredMap, PolygonArea } from 'components/map';

import { createMutationAtom } from 'atoms/create-resource-atom';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ColumnDependency } from '@carbon/react/icons';
import style from '../calculation/asset.module.scss';
import { green } from '@carbon/colors';
import {
  depthRangesMapAtom,
  tractsMapAtom,
  subtractsAtom,
  subtractCurrentDepthRangeMapAtom,
} from './atoms';
import { atomWithStorage } from 'jotai/utils';
import { getMapDefaultConfig } from 'components/map/atoms';
import { InlineEdit } from 'components/inline-edit';

import { DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';

export interface TractProps {
  onUpdate: () => void;
  tractHref: string;
  id: string;
}

const ChainViewer = lazy(() => import('../calculation/chain_viewer'));

const mutationAtom = createMutationAtom<
  TractResponse,
  'workspaceCalculationTract'
>();

const mapConfigAtom = atomWithStorage(
  'insights-tract-map-config',
  getMapDefaultConfig()
);

export function Tract({ onUpdate, tractHref }: TractProps) {
  const [depthTab, setDepthTab] = useAtom(subtractCurrentDepthRangeMapAtom);
  const subtracts = useAtomValue(subtractsAtom);
  const [graphOpen, setGraphOpen] = useState(false);
  const [, runMutation] = useAtom(mutationAtom);
  const [tractMap, setTractMap] = useAtom(tractsMapAtom);
  const setDepthRange = useSetAtom(depthRangesMapAtom);
  const [name, _setName] = useState('');
  const [legalAcreage, _setLegalAcreage] = useState('');
  const nameRef = useRef(name);
  const legalAcreageRef = useRef(legalAcreage);
  const setName = (v: string) => {
    nameRef.current = v;
    _setName(v);
  };
  const setLegalAcreage = (v: string) => {
    legalAcreageRef.current = v;
    _setLegalAcreage(v);
  };

  useEffect(() => {
    setTractMap({ href: tractHref });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tract = tractMap[tractHref];

  useEffect(() => {
    if (tract?.data && !('error' in tract.data)) {
      setName(tract.data.overriddenName || tract.data.derivedName);
      setLegalAcreage(
        tract.data.legalAcreage !== null ? String(tract.data.legalAcreage) : ''
      );
    }
  }, [tract?.data]);

  if (tract && tract.loading) {
    return <OutputSkeleton />;
  }

  if (!tract) return null;

  if (tract.data && 'error' in tract.data)
    return (
      <div className={style.assetData}>
        <InlineNotification kind="error" title={tract.data.error} lowContrast />
      </div>
    );

  if (!tract.data) return null;

  const tractId = tract.data.id;
  const derivedName = tract.data.derivedName;

  return (
    <section>
      <div className={style.assetGrid}>
        <div className={style.assetData}>
          <TypeLayout>
            <TypeLayoutBody>
              <TypeLayoutRow>
                <TypeLayoutCell className={style.colLeft}>
                  Original Sub-tract Name
                </TypeLayoutCell>
                <TypeLayoutCell className={style.colRight}>
                  {tract.data.derivedName}
                </TypeLayoutCell>
              </TypeLayoutRow>
              <TypeLayoutRow>
                <TypeLayoutCell className={style.colLeft}>
                  Sub-tract Name
                </TypeLayoutCell>
                <TypeLayoutCell className={style.colRight}>
                  <InlineEdit
                    id={`${tract.data.id}-name`}
                    value={name}
                    labelText="Sub-tract Name"
                    editLabel="Edit"
                    onChange={(val: string) => setName(val)}
                    onCancel={(initialVal: string) => {
                      setName(initialVal);
                      requestAnimationFrame(() => {
                        if (document.activeElement instanceof HTMLElement) {
                          document.activeElement.blur();
                        }
                      });
                    }}
                    saveLabel="Save"
                    cancelLabel="Cancel"
                    editAlwaysVisible
                    onSave={() =>
                      runMutation({
                        url: tractHref,
                        type: 'PATCH',
                        data: { overriddenName: nameRef.current },
                        onSuccess: () => {
                          onUpdate();
                          setTractMap({ href: tractHref, force: true });
                        },
                      })
                    }
                    placeholder=""
                    size="sm"
                  />
                </TypeLayoutCell>
              </TypeLayoutRow>
              <TypeLayoutRow>
                <TypeLayoutCell className={style.colLeft}>
                  GIS Gross Acreage
                </TypeLayoutCell>
                {tract.data.calculatedAcreage && (
                  <TypeLayoutCell className={style.colRight}>
                    {DECIMAL_FORMATTER_TWO_FRACTIONS.format(
                      tract.data.calculatedAcreage
                    )}
                  </TypeLayoutCell>
                )}
              </TypeLayoutRow>
              <TypeLayoutRow>
                <TypeLayoutCell className={style.colLeft}>
                  Legal Acreage:
                </TypeLayoutCell>
                <TypeLayoutCell className={style.colRight}>
                  <InlineEdit
                    id={`${tract.data.id}-legal-acreage`}
                    value={legalAcreage}
                    labelText="Legal Acreage"
                    editLabel="Edit"
                    onChange={(val: string) => setLegalAcreage(val)}
                    onCancel={(initialVal: string) => {
                      setLegalAcreage(initialVal);
                      requestAnimationFrame(() => {
                        if (document.activeElement instanceof HTMLElement) {
                          document.activeElement.blur();
                        }
                      });
                    }}
                    saveLabel="Save"
                    cancelLabel="Cancel"
                    editAlwaysVisible
                    onSave={() => {
                      const floatInput = parseFloat(legalAcreageRef.current);
                      const parsed = Number.isNaN(floatInput)
                        ? null
                        : floatInput;
                      runMutation({
                        url: tractHref,
                        type: 'PATCH',
                        data: { legalAcreage: parsed },
                        onSuccess: () => {
                          onUpdate();
                          setTractMap({ href: tractHref, force: true });
                        },
                      });
                    }}
                    placeholder=""
                    size="sm"
                  />
                </TypeLayoutCell>
              </TypeLayoutRow>
            </TypeLayoutBody>
          </TypeLayout>
        </div>
        <div className={style.assetMap}>
          {tract.data.geometry ? (
            <CenteredMap
              geometry={tract.data.geometry}
              mapConfigAtom={mapConfigAtom}
            >
              {subtracts.map(
                (el) =>
                  el.geometry && (
                    <PolygonArea
                      key={el.id}
                      geometry={el.geometry}
                      id={el.id}
                      color={el.id === tractId ? green[50] : undefined}
                    />
                  )
              )}
            </CenteredMap>
          ) : (
            <p>
              There was an issue rendering the map. The tract has no geometry.
            </p>
          )}
        </div>
      </div>
      <hr className={style.separator} />
      <div className={style.tabsContained}>
        <Tabs
          key={tractId}
          selectedIndex={depthTab[tractId] ?? 0}
          onChange={({ selectedIndex }) => {
            setDepthTab({ subtractId: tractId, depthIndex: selectedIndex });
            if (selectedIndex > 0) {
              const depthRange = (tract.data as any)?.depthRanges?.[
                selectedIndex - 1
              ];
              if (depthRange) {
                setDepthRange({ href: depthRange.href });
              }
            }
          }}
        >
          <TabList contained>
            <Tab>Surface</Tab>
            {tract.data.depthRanges.map((depthRange) => (
              <Tab key={depthRange.name}>{depthRange.name}</Tab>
            ))}
          </TabList>
          <TabPanels>
            <TabPanel>
              {tract.data.output ? (
                <>
                  {tract.data.resources.debuggingGraph?.href && (
                    <>
                      <div className={style.popupBtnRow}>
                        <Button
                          kind="ghost"
                          renderIcon={ColumnDependency}
                          onClick={() => setGraphOpen(true)}
                        >
                          Open surface graph
                        </Button>
                      </div>
                      {graphOpen && (
                        <Suspense fallback={<div>Loading</div>}>
                          <ChainViewer
                            tractName={tract.data.derivedName}
                            url={tract.data.resources.debuggingGraph.href}
                            onClose={() => setGraphOpen(false)}
                            isSurface
                          />
                        </Suspense>
                      )}
                    </>
                  )}
                  <Output
                    type="surface"
                    graph={tract.data.resources.debuggingGraph?.href ?? null}
                    output={tract.data.output}
                    tractName={tract.data.derivedName}
                    outputRows={tract.data.outputRows}
                    mineralLeaseholdRows={[]}
                  />
                </>
              ) : (
                <CalculationLoading />
              )}
            </TabPanel>
            {tract.data.depthRanges.map((depthRange) => (
              <TabPanel key={depthRange.name}>
                <DepthRange href={depthRange.href} tractName={derivedName} />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </div>
    </section>
  );
}
